<template>
  <div class="import-users">
    <div class="actions-row">
      <label class="file-label">
        <span>Välj CSV- eller Excel-fil</span>
        <input type="file" accept=".csv,.xlsx,.xls" @change="handleFileUpload" />
      </label>
      <button class="btn-import" :disabled="!parsedData || importing" @click="importUsers">
        {{ importing ? "Importerar..." : "Importera användare" }}
      </button>
      <button class="btn-example" @click="downloadExampleCSV">
        ⬇ Ladda ner exempel-CSV
      </button>
    </div>

    <div v-if="statusMessage" class="status-message" :class="statusType">
      {{ statusMessage }}
    </div>

    <div v-if="parsedData" class="preview">
      <p>{{ parsedData.length }} rad(er) inlästa — redo att importera.</p>
    </div>
  </div>
</template>

<script>
import Papa from "papaparse";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "@/firebase";

export default {
  data() {
    return {
      parsedData: null,
      importing: false,
      statusMessage: "",
      statusType: "info",
    };
  },
  methods: {
    // ---------------------------------------------------------------------------
    // File handling — CSV or Excel
    // ---------------------------------------------------------------------------
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.statusMessage = "";
      this.parsedData = null;

      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "csv") {
        this.parseCSV(file);
      } else if (ext === "xlsx" || ext === "xls") {
        await this.parseExcel(file);
      } else {
        this.setStatus("Filtypen stöds inte. Använd .csv, .xlsx eller .xls.", "error");
      }
    },

    async parseCSV(file) {
      try {
        const buffer = await file.arrayBuffer();
        const text = this.decodeCsvBuffer(buffer);
        Papa.parse(text, {
          header: true,
          delimiter: ";",
          skipEmptyLines: true,
          complete: (results) => {
            this.parsedData = results.data;
            this.setStatus(
              `${results.data.length} rader inlästa från CSV.`,
              "info"
            );
          },
          error: (err) => {
            this.setStatus(
              "Kunde inte läsa CSV-filen: " + err.message,
              "error"
            );
          },
        });
      } catch (err) {
        this.setStatus("Kunde inte läsa CSV-filen: " + err.message, "error");
      }
    },

    /**
     * Decode a CSV ArrayBuffer with encoding auto-detection.
     * Tries UTF-8 first (the modern default), then falls back to
     * Windows-1252 (a superset of ISO-8859-1) which is what Excel on
     * Windows typically exports CSV as. Without this, Swedish characters
     * (å ä ö) get rendered as the U+FFFD replacement character.
     */
    decodeCsvBuffer(buffer) {
      const bytes = new Uint8Array(buffer);

      // Strip UTF-8 BOM if present
      let start = 0;
      if (
        bytes.length >= 3 &&
        bytes[0] === 0xef &&
        bytes[1] === 0xbb &&
        bytes[2] === 0xbf
      ) {
        start = 3;
      }
      const payload = bytes.subarray(start);

      // Attempt strict UTF-8 — throws on invalid byte sequences
      try {
        const utf8 = new TextDecoder("utf-8", { fatal: true }).decode(payload);
        if (!utf8.includes("\uFFFD")) return utf8;
      } catch (_) {
        // Invalid UTF-8 — fall through to the legacy encoding
      }

      // Fall back to Windows-1252 (Excel's typical CSV output on Windows)
      return new TextDecoder("windows-1252").decode(payload);
    },

    async parseExcel(file) {
      try {
        const XLSX = await this.loadSheetJS();
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        this.parsedData = rows;
        this.setStatus(`${rows.length} rader inlästa från Excel.`, "info");
      } catch (err) {
        this.setStatus("Kunde inte läsa Excel-filen: " + err.message, "error");
      }
    },

    loadSheetJS() {
      return new Promise((resolve, reject) => {
        if (window.XLSX) {
          resolve(window.XLSX);
          return;
        }
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        script.onload = () => resolve(window.XLSX);
        script.onerror = () => reject(new Error("Kunde inte ladda SheetJS från CDN."));
        document.head.appendChild(script);
      });
    },

    // ---------------------------------------------------------------------------
    // Example CSV download
    // ---------------------------------------------------------------------------
    downloadExampleCSV() {
      const rows = [
        "firstName;lastName;email;mobile;userEnabled;assignedTableName",
        "Anna;Svensson;anna@example.com;+46701234567;sant;Table 1",
        "Erik;Lindgren;erik@example.com;+46709876543;sant;Bord 2",
        "Maria;Johansson;maria@example.com;+46701112233;sant;3",
      ];
      // Prepend UTF-8 BOM so Excel opens the file with correct encoding
      // and doesn't mangle å ä ö on re-save.
      const csvContent = "\uFEFF" + rows.join("\r\n");
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "import_users_example.csv";
      a.click();
      URL.revokeObjectURL(url);
    },

    // ---------------------------------------------------------------------------
    // Table name normalisation
    // ---------------------------------------------------------------------------
    normalizeTableName(raw) {
      if (!raw) return null;
      const trimmed = String(raw).trim();
      if (/^\d+$/.test(trimmed)) return `Table ${trimmed}`;
      const bordMatch = trimmed.match(/^bord\s+(\d+)$/i);
      if (bordMatch) return `Table ${bordMatch[1]}`;
      const tableMatch = trimmed.match(/^table\s+(\d+)$/i);
      if (tableMatch) return `Table ${tableMatch[1]}`;
      return trimmed;
    },

    async resolveTable(tableName) {
      const tablesRef = collection(db, "tables");
      const tableQuery = query(tablesRef, where("name", "==", tableName));
      const tableSnapshot = await getDocs(tableQuery);
      if (!tableSnapshot.empty) {
        return tableSnapshot.docs[0].id;
      }
      console.log(`Table "${tableName}" saknas, skapar den.`);
      const newTableRef = await addDoc(tablesRef, { name: tableName, users: [] });
      return newTableRef.id;
    },

    // ---------------------------------------------------------------------------
    // Existing-user lookup
    //
    // Matching priority:
    //   1. email   (most unique, tolerant of name changes / mis-spellings)
    //   2. mobile  (already normalised with leading "+")
    //   3. firstName + lastName  (fallback when neither e-post/telefon finns)
    //
    // Using email/mobile first also lets us recover rows that were imported
    // earlier with a broken encoding — e.g. an old "Westr�us" row will now
    // match on email and get its namn-fält corrected to "Westræus".
    // ---------------------------------------------------------------------------
    async findExistingUser(usersRef, user) {
      const email = (user.email || "").trim();
      const mobile = (user.mobile || "").trim();

      if (email) {
        // Prefer the case-insensitive lookup on emailLower.
        const emailLower = email.toLowerCase();
        const lowerSnap = await getDocs(
          query(usersRef, where("emailLower", "==", emailLower))
        );
        if (!lowerSnap.empty) return lowerSnap.docs[0];

        // Fallback: legacy records imported before emailLower existed —
        // match on the raw email field as it was originally stored.
        const rawSnap = await getDocs(
          query(usersRef, where("email", "==", email))
        );
        if (!rawSnap.empty) return rawSnap.docs[0];
      }

      if (mobile) {
        const mobileSnap = await getDocs(
          query(usersRef, where("mobile", "==", mobile))
        );
        if (!mobileSnap.empty) return mobileSnap.docs[0];
      }

      if (user.firstName && user.lastName) {
        const nameSnap = await getDocs(
          query(
            usersRef,
            where("firstName", "==", user.firstName),
            where("lastName", "==", user.lastName)
          )
        );
        if (!nameSnap.empty) return nameSnap.docs[0];
      }

      return null;
    },

    // ---------------------------------------------------------------------------
    // Import
    // ---------------------------------------------------------------------------
    async importUsers() {
      if (!this.parsedData) {
        this.setStatus("Ladda upp en fil först.", "error");
        return;
      }
      this.importing = true;
      this.setStatus("Importerar...", "info");

      let created = 0;
      let updated = 0;
      let skipped = 0;

      try {
        for (const user of this.parsedData) {
          if (!user.firstName && !user.lastName && !user.email) {
            skipped++;
            continue;
          }

          if (user.mobile && !user.mobile.startsWith("+")) {
            user.mobile = `+${user.mobile}`;
          }

          const normalizedTableName = this.normalizeTableName(user.assignedTableName);
          if (!normalizedTableName) {
            console.warn("Inget bord angivet för:", user);
            skipped++;
            continue;
          }

          const assignedTableId = await this.resolveTable(normalizedTableName);

          const userEnabled = ["true", "sant"].includes(
            user.userEnabled?.toLowerCase()
          );

          const trimmedEmail = (user.email || "").trim();
          const userData = {
            firstName: user.firstName,
            lastName: user.lastName,
            email: trimmedEmail,
            // Lowercase duplicate for case-insensitive lookup on re-import.
            emailLower: trimmedEmail.toLowerCase(),
            fullNameLower: `${user.firstName.toLowerCase()} ${user.lastName.toLowerCase()}`,
            mobile: user.mobile,
            userEnabled,
            assignedTable: assignedTableId,
            assignedTableName: normalizedTableName,
          };

          const usersRef = collection(db, "users");
          const existingDoc = await this.findExistingUser(usersRef, user);

          if (existingDoc) {
            const userDocRef = doc(db, "users", existingDoc.id);
            await updateDoc(userDocRef, userData);
            updated++;
          } else {
            await addDoc(usersRef, userData);
            created++;
          }
        }

        this.setStatus(
          `Klart! ${created} skapade, ${updated} uppdaterade, ${skipped} hoppades över.`,
          "success"
        );
      } catch (error) {
        console.error("Import-fel:", error);
        this.setStatus("Ett fel uppstod under importen: " + error.message, "error");
      } finally {
        this.importing = false;
      }
    },

    setStatus(message, type = "info") {
      this.statusMessage = message;
      this.statusType = type;
    },
  },
};
</script>

<style scoped>
.import-users {
  padding: 1rem 0;
}

.actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
}

.file-label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #555;
}

.file-label input {
  font-size: 0.875rem;
}

.btn-import,
.btn-example {
  padding: 0.5rem 1.2rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
}

.btn-import {
  background-color: #c39935;
  color: #fff;
}

.btn-import:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-example {
  background-color: #f0f0f0;
  color: #333;
}

.btn-example:hover {
  background-color: #e0e0e0;
}

.status-message {
  padding: 0.6rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
}

.status-message.info    { background: #e8f4fd; color: #1a6fa8; }
.status-message.success { background: #e6f4ea; color: #2d7a3a; }
.status-message.error   { background: #fde8e8; color: #a81a1a; }

.preview {
  font-size: 0.875rem;
  color: #666;
}
</style>
